import React, { useMemo, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { usePostHogQuery } from '../../hooks/usePostHogQuery';
import { buildTrendsQuery } from '../../utils/posthogApi';
import KpiCard from './components/KpiCard';
import TrendChart from './components/TrendChart';
import DataTable from './components/DataTable';
import { formatDate } from './utils';

const POSTHOG_ERROR_TRACKING_URL =
  'https://us.posthog.com/project/280011/error_tracking?filterTestAccounts=true';

const HealthErrorsView = ({ dateRange }) => {
  const dateFrom = dateRange || '-30d';
  const [hideRealtimeNoise, setHideRealtimeNoise] = useState(true);

  // ── Error volume over time ──
  const { data: errorTrend, loading: errorTrendLoading } = usePostHogQuery(
    () => buildTrendsQuery(['$exception'], dateFrom),
    { deps: [dateFrom] }
  );

  // ── Rage clicks by page ──
  const { data: rageTrend, loading: rageTrendLoading } = usePostHogQuery(
    () => buildTrendsQuery(
      [{ event: '$rageclick', math: 'total' }],
      dateFrom,
      { breakdownBy: { breakdown: '$pathname', breakdown_type: 'event' } }
    ),
    { deps: [dateFrom] }
  );

  // ── Error tracking issues (via ErrorTrackingQuery) ──
  const { data: errorIssues, loading: issuesLoading } = usePostHogQuery(
    () => ({
      query: {
        kind: 'ErrorTrackingQuery',
        dateRange: { date_from: dateFrom },
        orderBy: 'occurrences',
        volumeResolution: 30,
        limit: 50,
      },
    }),
    { deps: [dateFrom] }
  );

  // ── App stability: opened vs backgrounded ──
  const { data: stabilityData, loading: stabilityLoading } = usePostHogQuery(
    () => buildTrendsQuery(
      [
        { event: 'Application Opened', math: 'total' },
        { event: 'Application Backgrounded', math: 'total' },
      ],
      dateFrom
    ),
    { deps: [dateFrom] }
  );

  // ── Derived data ──

  const errorChartData = useMemo(() => {
    if (!errorTrend?.results?.[0]) return [];
    const series = errorTrend.results[0];
    return (series.labels || []).map((label, i) => ({
      date: formatDate(label),
      errors: series.data?.[i] ?? 0,
    }));
  }, [errorTrend]);

  const totalErrors = errorTrend?.results?.[0]?.count ?? 0;
  const totalRageClicks = rageTrend?.results?.reduce(
    (sum, series) => sum + (series.count ?? 0), 0
  );

  const errorRows = useMemo(() => {
    const issues = errorIssues?.results || [];
    if (!Array.isArray(issues)) return [];

    let filtered = issues;
    if (hideRealtimeNoise) {
      filtered = issues.filter(
        (issue) => !issue.description?.includes('RealtimeSubscribeException')
      );
    }

    return filtered.map((issue) => {
      const occurrences = issue.aggregations?.occurrences ?? 0;
      const users = issue.aggregations?.users ?? 0;
      return {
        id: issue.id,
        title: truncate(issue.description || issue.name || 'Unknown', 60),
        occurrences,
        users,
        firstSeen: issue.first_seen ? formatDate(issue.first_seen) : '—',
        lastSeen: issue.last_seen ? formatDate(issue.last_seen) : '—',
        severity: occurrences > 100 ? 'high' : occurrences > 10 ? 'medium' : 'low',
      };
    });
  }, [errorIssues, hideRealtimeNoise]);

  const rageClickRows = useMemo(() => {
    if (!rageTrend?.results) return [];
    return rageTrend.results
      .map((series) => ({
        page: series.breakdown_value || series.label || 'Unknown',
        count: series.count ?? 0,
      }))
      .filter((r) => r.count > 0)
      .sort((a, b) => b.count - a.count)
      .slice(0, 15);
  }, [rageTrend]);

  const appOpened = stabilityData?.results?.[0]?.count ?? 0;
  const appBackgrounded = stabilityData?.results?.[1]?.count ?? 0;

  const errorColumns = [
    { key: 'title', label: 'Error' },
    { key: 'occurrences', label: 'Count', render: (v) => v.toLocaleString() },
    {
      key: 'severity',
      label: 'Severity',
      render: (v) => <span className={`bi-error-badge ${v}`}>{v}</span>,
    },
    { key: 'users', label: 'Users' },
    { key: 'lastSeen', label: 'Last Seen' },
  ];

  const rageColumns = [
    { key: 'page', label: 'Page' },
    { key: 'count', label: 'Rage Clicks', render: (v) => v.toLocaleString() },
  ];

  return (
    <div className="bi-health">
      <div className="bi-view-actions">
        <a
          href={POSTHOG_ERROR_TRACKING_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="bi-posthog-link"
        >
          View in PostHog
          <ExternalLink size={13} />
        </a>
      </div>

      <div className="bi-kpi-grid">
        <KpiCard title="Total Errors" value={totalErrors} loading={errorTrendLoading} />
        <KpiCard title="Rage Clicks" value={totalRageClicks} loading={rageTrendLoading} />
        <KpiCard title="App Opens" value={appOpened} loading={stabilityLoading} />
        <KpiCard
          title="Open/Background Ratio"
          value={appBackgrounded > 0 ? (appOpened / appBackgrounded).toFixed(2) : '—'}
          loading={stabilityLoading}
        />
      </div>

      <TrendChart
        title="Error Volume"
        data={errorChartData}
        lines={[{ key: 'errors', label: 'Exceptions', color: '#ef4444' }]}
        loading={errorTrendLoading}
      />

      <div style={{ marginBottom: '0.5rem' }}>
        <label style={{ fontSize: 12, color: 'var(--admin-gray-500)', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={hideRealtimeNoise}
            onChange={(e) => setHideRealtimeNoise(e.target.checked)}
            style={{ marginRight: 6 }}
          />
          Hide RealtimeSubscribeException noise
        </label>
      </div>
      <DataTable
        title="Top Error Issues"
        columns={errorColumns}
        data={errorRows}
        loading={issuesLoading}
      />

      <DataTable
        title="Rage Clicks by Page"
        columns={rageColumns}
        data={rageClickRows}
        loading={rageTrendLoading}
        emptyMessage="No rage clicks detected"
      />
    </div>
  );
};

function truncate(str, len) {
  return str.length > len ? str.slice(0, len) + '...' : str;
}

export default HealthErrorsView;
